import { ErrorBoundary } from "@/components/ErrorBoundary";
import "@/App.css";
import { lazy, Suspense, type FC } from "react";
import { MobileOverlayProvider } from "@/contexts/MobileOverlayContext";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Agentation } from "agentation";
import SEOHead from "@/components/SEOHead";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileBottomBar from "@/components/MobileBottomBar";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdvertiserRoute from "@/components/AdvertiserRoute";
import AdminRoute from "@/components/AdminRoute";
import PublicarRedirect from "@/components/PublicarRedirect";
import Home from "@/pages/Home";
import { Toaster } from "sonner";
import PrivacyGate from "@/components/PrivacyGate";
import CookieConsent from "@/components/CookieConsent";
import UserActivityHandler from "@/components/UserActivityHandler";
import ScrollToTop from "@/components/ScrollToTop";
import FloatingScrollButton from "@/components/FloatingScrollButton";

export function isProfileDetailRoute(pathname: string): boolean {
  return pathname.startsWith("/perfil/") || pathname.startsWith("/acompanhante/");
}

function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith("/admin");
}

function LegacyAcompanhantesRedirect() {
  const { category, city } = useParams();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  if (category) search.set("category", category as string);
  if (city) search.set("city", city as string);
  const qs = search.toString();
  return <Navigate to={`/${qs ? `?${qs}` : ""}`} replace />;
}

const ProfileDetail = lazy(() => import("@/pages/ProfileDetail"));
const ProfilePrivate = lazy(() => import("@/pages/ProfilePrivate"));
const ProfileBookings = lazy(() => import("@/pages/ProfileBookings"));
const ProfileTour = lazy(() => import("@/pages/TourMode"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ListingStats = lazy(() => import("@/pages/dashboard/ListingStats"));
const Publish = lazy(() => import("@/pages/Publish"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Verification = lazy(() => import("@/pages/Verification"));
const Plan = lazy(() => import("@/pages/Plan"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Admin = lazy(() => import("@/pages/Admin"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const Articles = lazy(() => import("@/pages/Articles"));
const ArticleDetail = lazy(() => import("@/pages/ArticleDetail"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));

function RouteFallback() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="border border-cream-200 bg-cream-50 h-64 animate-pulse" />
    </div>
  );
}

const App: FC = () => {
  return (
    <ErrorBoundary>
      <div className="App">
         <HelmetProvider>
           <ThemeProvider>
              <AuthProvider>
                <MobileOverlayProvider>
                  <BrowserRouter>
                    <SEOHead />
                    <Navbar />
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    {/* /acompanhantes removido — redireciona para Home preservando filtros */}
                    <Route path="/acompanhantes" element={<LegacyAcompanhantesRedirect />} />
                    <Route path="/acompanhantes/:category" element={<LegacyAcompanhantesRedirect />} />
                    <Route path="/acompanhantes/:category/:city" element={<LegacyAcompanhantesRedirect />} />
                    {/* URL pública amigável: /perfil/{state}/{city}/{name-slug-shortid} */}
                    <Route path="/perfil/:state/:city/:slug" element={<ProfileDetail />} />
                    {/* Compat: URL legada por id continua funcionando para links antigos. */}
                    <Route path="/acompanhante/:id" element={<ProfileDetail />} />
                    <Route path="/perfil/:slug/privado" element={<ProfilePrivate />} />
                    <Route path="/perfil/:slug/agendar" element={<ProfileBookings />} />
                    <Route path="/perfil/:slug/tour" element={<ProfileTour />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/registrar" element={<Register />} />
                    <Route path="/esqueci-senha" element={<ResetPassword />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/como-funciona" element={<HowItWorks />} />
                    <Route path="/fale-conosco" element={<Contact />} />
                    <Route path="/artigos" element={<Articles />} />
                    <Route path="/artigos/:slug" element={<ArticleDetail />} />
                    <Route path="/dashboard" element={<AdvertiserRoute><Dashboard /></AdvertiserRoute>} />
                    <Route path="/dashboard/stats/:id" element={<ProtectedRoute><ListingStats /></ProtectedRoute>} />
                    <Route path="/publicar" element={<PublicarRedirect><Publish /></PublicarRedirect>} />
                    <Route path="/conta/verificacao" element={<ProtectedRoute><Verification /></ProtectedRoute>} />
                    <Route path="/conta/plano" element={<ProtectedRoute><Plan /></ProtectedRoute>} />
                    <Route path="/sucesso" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                    <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                    <Route path="/termos" element={<Terms />} />
                    <Route path="/privacidade" element={<Privacy />} />

                    {/* SEO Routes — agora renderizam Home com filtros por estado/cidade */}
                    <Route path="/:stateSlug" element={<Home />} />
                    <Route path="/:stateSlug/:citySlug" element={<Home />} />
                  </Routes>
                </Suspense>
                <Footer />
                <ConditionalMobileBottomBar />
                <PrivacyGate />
                <CookieConsent />
                <Toaster richColors position="top-right" />
                <UserActivityHandler />
                <ScrollToTop />
                <FloatingScrollButton />
                {import.meta.env.DEV && (
                  <Agentation endpoint="http://localhost:4747" />
                )}
              </BrowserRouter>
                </MobileOverlayProvider>
              </AuthProvider>
          </ThemeProvider>
        </HelmetProvider>
      </div>
    </ErrorBoundary>
  );
};

function ConditionalMobileBottomBar() {
  const location = useLocation();
  if (isProfileDetailRoute(location.pathname) || isAdminRoute(location.pathname)) return null;
  return <MobileBottomBar />;
}

export default App;
